/**
 * Thin typed web API client for the Imports feature (web/v2).
 *
 * This is the ONE client all five Phase-1 web slices (03–07) share — the
 * real-client counterpart the MSW handlers mock. As of subtask 08 it runs
 * against the LIVE FastAPI surface over `VITE_API_URL` by default; MSW only
 * intercepts when `VITE_USE_MSW` is set (component tests + opt-in dev), so the
 * real-stack/E2E build talks to the real API with NO call-site changes
 * (DECIDED — EPIC Open-question #2: client home is 01).
 *
 * It is PURELY ADDITIVE (FR-09): it does not replace or modify the extension's
 * `nucceClient` or any existing web client. Cookie auth rides via
 * `credentials: "include"` through the shared `makeAuthedFetch` lifecycle
 * helper (`@nu-assist/shared-fe`) by default — single-flight 401 → `POST
 * /auth/refresh` → retry-once, same contract `AuthGuard`'s `useMe()` uses.
 *
 * Bug fixed 2026-07-05: every call site previously left `fetchImpl` unset,
 * so every request went through bare `globalThis.fetch` with NO refresh-on-
 * 401 handling — a plain, uncaught 401 whenever the 1h access-token cookie
 * expired mid-session, surfacing to the user as "Couldn't load imports.
 * Please try again." (`ImportsPage.tsx`'s `query.isError` branch). Confirmed
 * live against `dev`'s API logs: repeated 401s on `/api/web/v2/imports/*`
 * immediately followed by 200s once something else (e.g. a concurrent
 * `useMe()` 401) triggered the SAME module-scoped single-flight refresh in
 * `@nu-assist/shared-fe`'s `session.ts` — explaining why it was intermittent
 * (a React Query retry sometimes landed after the cookie refreshed, sometimes
 * didn't) rather than a hard, deterministic failure.
 *
 * Every function is typed end-to-end against `@nu-assist/contracts`, so a
 * backend schema change surfaces at `tsc` time across all consumers.
 */

import type {
  AcceptedJob,
  AddAccepted,
  AddPreview,
  AddToSearchRequest,
  AddToThriveRequest,
  AvatarPresignResponse,
  BulkLeaveOutRequest,
  BulkLeaveOutResult,
  CombineAccepted,
  CompareRequest,
  CompareResult,
  CompareStatusItem,
  DownloadLink,
  ExportStatus,
  ImportCompleteRequest,
  ImportContactRow,
  ImportContactsResponse,
  ImportCreateRequest,
  ImportCreated,
  ImportDetail,
  ImportLineage,
  ImportListResponse,
  ImportOwnersResponse,
  ImportRenamed,
  ImportStatus,
  ImportTag,
  ThrivePreviewResponse,
  ThriveSearchImportAccepted,
  ThriveSearchImportRequest,
  components,
} from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";
import { clearStoredSelectedImport } from "../lib/selectedImportStorage";

// ---------------------------------------------------------------------------
// Conflict-action wire types (epic 2026-09-04 sub-task 04).
//
// LOCAL, TEMPORARY: the OpenAPI re-emit + contracts codegen happen ONCE on the
// integration branch (not per sub-task), so the generated types for the three
// new endpoints don't exist yet. When `@nu-assist/contracts` regenerates,
// these move there and this block is deleted. Shapes mirror
// `packages/api/app/web/v2/imports/schemas.py` field-for-field.
// ---------------------------------------------------------------------------

/** Mirror of the API's `LeaveOutState` (PUT/DELETE .../leave-out). */
export interface LeaveOutState {
  import_contact_id: string;
  left_out: boolean;
  left_out_by: string | null;
  left_out_at: string | null;
}

/** Mirror of the API's `DifferentPersonExclusion` (POST .../different-person). */
export interface DifferentPersonExclusion {
  linkedin_url_canonical: string;
  thrive_id: string;
  created_by: string;
  created_at: string;
  already_existed: boolean;
}

/** `CompareResult` + the additive `left_out_contact_ids` field the API now
 *  returns (read live at poll time). Optional so a stale generated type — or a
 *  cached pre-upgrade response — narrows safely. */
export type CompareResultWithConflictActions = CompareResult & {
  left_out_contact_ids?: string[];
};

/** Mirror of the API's `DifferentPersonUndone` (DELETE .../different-person).
 *  Generated too, but declared beside its siblings above so the three
 *  registry-action shapes are read in one place. */
export interface DifferentPersonUndone {
  linkedin_url_canonical: string;
  thrive_id: string;
  removed: boolean;
}

/**
 * `POST .../contacts/{contact_id}/recheck` (fu5 sub-task 02, Part B).
 *
 * ALIASED FROM THE GENERATED SCHEMA, not hand-mirrored like the three shapes
 * above: this one IS already in `packages/contracts/src/openapi.ts`, and the
 * `components` map is the documented escape hatch for a schema that has no
 * named alias in `contracts`' index yet. The alias only resolves while the
 * schema exists, so drift surfaces at `tsc` time instead of at runtime.
 *
 * READ `outcome`, NEVER `resolved`. `resolved` is true only for `cleared`; it
 * is FALSE for BOTH `still_colliding` and `check_failed`, so it cannot tell
 * them apart — and confusing them shakes the card at a recruiter who did
 * nothing wrong. A failed read is deliberately a 200 so it does not vanish into
 * this client's generic `ImportsApiError` path.
 */
export type DuplicateLinkedInRecheck = components["schemas"]["DuplicateLinkedInRecheck"];
/** The synchronous single-contact add. Same aliasing rule as above. */
export type SingleContactExport = components["schemas"]["SingleContactExport"];
// Re-exported from the contract, never re-spelled: the two values carry
// different thresholds, so a drifted copy would send the wrong rule silently.
export type { RecheckMode } from "@nu-assist/contracts";
import type { RecheckMode } from "@nu-assist/contracts";

// ---------------------------------------------------------------------------
// Combine & Filter (epic 2026-09-22). Sub-task 04 hand-mirrored these nine
// shapes while sub-task 01's routes were in flight (FastAPI emits a schema
// only once a wired route references it); 01 landed and the generated
// aliases now come from `@nu-assist/contracts` — re-exported here so the
// page and Import Detail keep importing them from the client module.
// ---------------------------------------------------------------------------

export type {
  BulkLeaveOutRequest,
  BulkLeaveOutResult,
  CombineAccepted,
  CombineDuplicateGroup,
  CombineDuplicateSource,
  CombineInheritedSearch,
} from "@nu-assist/contracts";

// Combine & Filter round 2 (R2-A backend + R2-B web, merged): every shape the
// page uses now comes from the regenerated contract.
export type {
  CombineFilter,
  CombineFilterClause,
  CombineFunnel,
  CombinePreviewContact,
  CombinePreviewRequest,
  CombinePreviewResponse,
  CombineRequest,
  CombineSourceState,
  ImportCompareState,
  ImportSummary,
} from "@nu-assist/contracts";
import type {
  CombinePreviewRequest,
  CombinePreviewResponse,
  CombineRequest,
} from "@nu-assist/contracts";

/** What the page hands `combinePreview`: the recipe plus optional paging.
 *  `page` / `page_size` / `view` (and the other defaulted fields) are marked
 *  optional here (rather than on the wire type above) because the server
 *  fills their Pydantic defaults, so callers may omit them — same ergonomic
 *  convenience the pre-R2 `CombinePreviewInput` offered over the recipe. */
type CombinePreviewDefaulted =
  | "page"
  | "page_size"
  | "view"
  | "remove_in_thrive"
  | "sort_key"
  | "sort_dir";
export type CombinePreviewInput = Omit<CombinePreviewRequest, CombinePreviewDefaulted> &
  Partial<Pick<CombinePreviewRequest, CombinePreviewDefaulted>>;

/** Minimal `fetch`-shaped function — `makeAuthedFetch(...)` by default (see
 *  `createImportsClient`); inject a different one (e.g. a test double) to
 *  override. */
export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface ImportsClientConfig {
  /** API origin (e.g. `import.meta.env.VITE_API_URL`). */
  apiBase: string;
  /** Defaults to `makeAuthedFetch({ apiBase, onSessionExpired })` — a hard
   *  session expiry (refresh cookie dead) hard-redirects to `/login`, same
   *  outcome `AuthGuard` already enforces independently. Inject to override
   *  (tests pass a plain mock `fetch`). */
  fetchImpl?: FetchLike;
}

export interface ListImportsParams {
  owner?: string;
  /**
   * One lifecycle tag, or several. An array is serialized as a REPEATED key
   * (`?tag=bct&tag=ai`) and the server ORs across the values — the imports
   * list's multi-select chips. The pickers still pass a single string; both
   * shapes describe the same endpoint.
   */
  tag?: string | readonly string[];
  q?: string;
  /** Restrict to the imports linked to one Thrive search (My Searches CTA). */
  search_id_thrive?: string;
  /**
   * Drop one parse status server-side. The BCT / AI Review pickers pass
   * `"failed"`. Subtractive rather than a `status` whitelist so `parsing` /
   * `uploaded` rows keep showing — those are usable shortly, and hiding them
   * reads as a lost file in the Baby Chops → import → BCT flow.
   */
  exclude_status?: ImportStatus;
  /**
   * Drop the imports carrying ANY of these lifecycle tags server-side. The
   * BCT picker passes `"bct"` — a BCT-derived import cannot seed another
   * tree — while AI Review omits it and keeps seeing those rows; every picker
   * adds `"combined"` under its Raw chip (a combined list carries `raw` too).
   * An array is serialized as a REPEATED key, like `tag`.
   */
  exclude_tag?: ImportTag | readonly ImportTag[];
  page?: number;
  page_size?: number;
  sort_key?: string;
  sort_dir?: "asc" | "desc";
}

export interface PageParams {
  page?: number;
  page_size?: number;
}

/** Contact-level `review_status` filter param — the server-derived status
 * each row carries (sub-task 10's contract). Single-select: the seven values
 * partition the import. Composes with `duplicate_linkedin` (an orthogonal
 * warning, not a status).
 *
 * The retired `export_status` param is still ACCEPTED by the API for one
 * release (mapped server-side to its nearest `review_status`), but this
 * client no longer sends it — new callers send `review_status`. */
export type ContactReviewStatusParam = NonNullable<ImportContactRow["review_status"]>;

/**
 * Server-side filters for `GET /imports/{id}/contacts` (2026-08-14 PRD). All
 * optional; `bct_*` apply only to BCT imports (the API silently ignores them
 * otherwise). `undefined` params are omitted from the query string entirely.
 */
export interface ContactsQueryParams extends PageParams {
  /** Case-insensitive substring over first OR last name. */
  q?: string;
  /** Case-insensitive substring over the contact's promoted role title.
   *  Applies to every import format (unlike `bct_*`). */
  current_position?: string;
  /** Case-insensitive substring over the contact's promoted employer. */
  current_company?: string;
  /** Case-insensitive substring over the BCT matched company. */
  bct_company?: string;
  /** Case-insensitive substring over the BCT matched position. */
  bct_position?: string;
  /** Keep rows whose BCT matched stint overlaps `[from, to]`. */
  bct_year_from?: number;
  bct_year_to?: number;
  review_status?: ContactReviewStatusParam;
  /** Warning filter chip, orthogonal to `review_status`: `true` narrows to
   *  rows whose LinkedIn URL carries a firm-wide 'Different person' exclusion.
   *  Omitted = all (the complement has no chip). */
  duplicate_linkedin?: boolean;
  /** The 'To review' chip's own predicate: `review_status = to_review` UNION
   *  the dup-LinkedIn warning, a row that is both counted once. Sent INSTEAD
   *  of `review_status=to_review` — sending both narrows to the intersection. */
  needs_review?: boolean;
  enrichment_status?: "failed";
  /** fu5 sub-task 01 — the split's two state axes plus the orthogonal mark.
   *
   *  These were MISSING from this interface while the page already sent them.
   *  `buildQuery` iterates `Object.entries`, so they still reached the wire —
   *  which is the dangerous shape of the bug: it works, and the type system
   *  vouches for nothing. The next edit to the filter object could drop one
   *  and `tsc` would stay silent.
   *
   *  `skipped: false` is a REAL narrowing ("the rows I have NOT skipped"), so
   *  it must survive `buildQuery`, which only omits `undefined`. */
  //
  //  DERIVED FROM THE CONTRACT, not re-typed. These two enums moved a value
  //  between them on 2026-09-10 (`needs_review` left the Thrive axis for the
  //  add-to-search one), and a hand-written copy went stale in the same commit
  //  — it accepted a value the API refuses and refused one it accepts, and
  //  `tsc` was happy because both halves were hand-written. Reading them off
  //  the generated row type means the next move cannot go unnoticed.
  thrive_id_state?: NonNullable<ImportContactRow["thrive_id_state"]>;
  add_to_search_state?: NonNullable<ImportContactRow["add_to_search_state"]>;
  skipped?: boolean;
  /**
   * Narrow to an explicit set of contacts (`?ids=<uuid>&ids=<uuid>` — the
   * repeated form `buildQuery` already emits for arrays). Used by the AI
   * Review preview to hydrate the contacts one run scored in ONE request,
   * rather than paging a whole import at `page_size <= 100` to find five rows.
   * Capped server-side at 25 — a WAF bound, not a page-size one: each id
   * costs 41 bytes of query string and `SizeRestrictions_QUERYSTRING`
   * blocks past 2,048. Combines with the other filters.
   */
  ids?: string[];
  /** Case-insensitive substring over the contact's email address. */
  q_email?: string;
  /** One Thrive candidacy stage, matched exactly (a label from
   *  `counts.stages`). Rows not pulled from a Thrive search match nothing. */
  stage?: string;
  /** Keywords searched in the latest applied AI review's notes (Trello #779):
   *  word-prefix, case-insensitive, `"quotes"` for a phrase. */
  q_notes?: string;
  /** How `q_notes` terms combine. Omitted = `any` (the page never sends it). */
  q_notes_mode?: "any" | "all";
  /** Column to sort by. Fixed: `name`, `company`, `position`, `email`, and on
   *  a Thrive-sourced list `stage` (along the ladder), `stage_since`,
   *  `added_at`. Dynamic: `score__<criterion>`. */
  sort_key?: string;
  sort_dir?: "asc" | "desc";
}

export class ImportsApiError extends Error {
  /** The server's `detail` when it was a STRING — the human-readable reason
   *  FastAPI puts on an `HTTPException` — or the `message` string of a
   *  structured (object) detail (see `detailData`). `undefined` when the body
   *  carried neither (a validation-error LIST, an ALB HTML page). Callers that
   *  act on the reason — the wizard's refusal state — must key on THIS, not on
   *  `status` alone.
   *
   *  **The string/list distinction is load-bearing and must stay here.**
   *  `useNewImport` reads `typeof detail === "string"` to tell "the server read
   *  your file and declined it" (terminal, no Retry) from "client/server
   *  drift" (retryable) — two 422s that mean opposite things to the uploader.
   *  Folding validation messages into this field would make every drift-422 a
   *  dead-end screen showing raw FastAPI text. They go in
   *  `validationMessages` instead. */
  public readonly detail?: string;

  /** FastAPI's validation `detail` list, flattened to its `msg` strings.
   *
   *  Populated INSTEAD of `detail`, never alongside it — the two are the two
   *  shapes one field arrives in, and keeping them apart is what lets a caller
   *  tell them apart. Present so a validation failure is readable rather than
   *  a bare status line: `CompareRequest.contact_ids`' cap is the first 422 a
   *  recruiter can provoke on purpose, and "Imports API request failed (422)"
   *  is no better than the CORS-less 403 the cap exists to replace. */
  public readonly validationMessages?: string[];

  /** FastAPI's `detail` when it is an OBJECT (a structured error), e.g. the
   *  Combine endpoints' 413 `{message, source_contacts_total,
   *  max_source_contacts}`. Its `message`, when a string, is also copied to
   *  `detail` so a caller that only shows text keeps working. */
  public readonly detailData?: Record<string, unknown>;

  constructor(
    public readonly status: number,
    public readonly url: string,
    detail?: string,
    validationMessages?: string[],
    detailData?: Record<string, unknown>
  ) {
    super(
      detail ??
        (validationMessages?.length
          ? validationMessages.join("; ")
          : `Imports API request failed (${status}) for ${url}`)
    );
    this.name = "ImportsApiError";
    this.detail = detail;
    this.validationMessages = validationMessages;
    this.detailData = detailData;
    Object.setPrototypeOf(this, ImportsApiError.prototype);
  }
}

const PREFIX = "/api/web/v2/imports";

/**
 * Max stored-avatar keys per `/avatars/presign` request. Each key is ~76 bytes
 * (`avatars/<sha256>.<ext>`), so a full 100-row page in ONE request serializes
 * to ~7.9 KB — only ~280 bytes under the shared ALB WAF `SizeRestrictions_BODY`
 * ~8 KB cap (see `avatars/service.py`), beyond which the request is 403'd at the
 * edge with a bare-HTML body that never reaches ECS logs. 50 keys ≈ 4 KB keeps a
 * comfortable margin; the endpoint de-dupes so chunking is free. Must stay ≤ the
 * server's `AvatarPresignRequest.s3_keys` `max_length`.
 */
const PRESIGN_MAX_KEYS_PER_REQUEST = 50;

/** A Thrive search option for the import Link-to-Search picker. */
export interface SearchOptionDTO {
  id: string;
  name: string;
  /** Thrive's own `job_title` attribute, separate from `name`. */
  job_title?: string | null;
  /** Thrive's `client_company.name`, separate from `name`. */
  company?: string | null;
  /** True when sourced from the dev/local TEST whitelist (empty in prod). */
  is_test?: boolean;
  /** Thrive's status, verbatim (`Open`, `Business Development`, `Closed`, `Lost`). */
  status?: string | null;
  /** Candidacies Thrive counts on the search (mirror, hourly); null when unknown. */
  candidacies_count?: number | null;
  /** When the search was created in Thrive (ISO 8601). */
  thrive_created_at?: string | null;
  /** The day the search closed; null when it has not. */
  closed_at?: string | null;
}
export interface SearchListResponseDTO {
  items: SearchOptionDTO[];
  /** Real total (whitelist + the count of Open and Business Development
   * searches in nu-assist's Thrive mirror, or the exact match count for a
   * typeahead query) — drives "Showing N of X". */
  total?: number;
}

export interface ListSearchesParams {
  /** Thrive search id (PREFIX — `441` reaches 4414) or case-insensitive
   * substring of the name, job title or client company. Any non-empty length:
   * the former "2+ chars" gate promoted a 1-char query to the unfiltered list
   * and rendered it as a match. */
  q?: string;
  /** Comma list of `open | bd | closed | lost`, or `all`. Omitted = Open +
   * Business Development, what every picker but the Thrive-source one wants. */
  statuses?: string;
}

/** A searches API status filter: one Thrive status, or every status. */
export type SearchStatusFilter = "closed" | "open" | "bd" | "lost" | "all";

/**
 * Serialize params to a query string, REPEATING the key for an array value.
 *
 * `?tag=bct&tag=ai` is the form FastAPI parses back into a list, and it is the
 * only reason this is not a one-liner over `search.set`. `set(key,
 * String(value))` stringifies an array through `Array.prototype.toString`,
 * producing `tag=bct,ai` — one value, comma included. Against the typed
 * `ImportTag` union that is a 422; before the param was typed it was a 200 that
 * matched nothing, which is the worse of the two.
 *
 * An EMPTY array contributes nothing, rather than a bare `tag=`: an empty
 * selection means no filter, and `?tag=` would arrive as one empty-string tag.
 */
function buildQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
      continue;
    }
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Body of `startCompare`. Every field is optional on the wire (the server fills
 * `skip_row_avatars`' default), but `openapi-typescript` emits a defaulted field
 * as REQUIRED — so this is `Partial` over the generated request, the same
 * convenience `CreateImportPayload` gives.
 */
export type CompareStartInput = Partial<CompareRequest>;

/**
 * Ergonomic create payload. `openapi-typescript` emits Pydantic fields that
 * have a server-side default (`import_format`, `source`) as REQUIRED (it does
 * not infer optionality from `default`). The server fills them, so the client
 * treats them as optional via `Partial` over the defaulted keys — additive
 * convenience over the generated contract; the wire still accepts the full
 * type. Truly-required fields (`filename`/`content_type`/`size_bytes`) stay
 * required.
 */
export type CreateImportPayload = Omit<ImportCreateRequest, "import_format" | "source"> &
  Partial<Pick<ImportCreateRequest, "import_format" | "source">>;

export function createImportsClient(cfg: ImportsClientConfig) {
  // Default `fetchImpl` to the session-enforcing `makeAuthedFetch` (single-
  // flight 401 -> POST /auth/refresh -> retry-once, shared module-scoped
  // state with `AuthGuard`'s own `useMe()` call — concurrent 401s from both
  // coalesce onto ONE refresh). Constructed once here, not per-request; safe
  // to build eagerly because `authedFetch`'s internal `fetch(...)` calls
  // resolve `globalThis.fetch` at CALL time (a bare identifier, not a
  // captured reference), so MSW's `server.listen()` patch — applied after
  // this client is constructed at module-eval time — is still honored.
  // `onSessionExpired` hard-redirects; `AuthGuard` independently reaches the
  // same conclusion on its own next check either way, so this is a
  // dependency-free backstop, not the sole safety net.
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => {
        // The shared import selection is persisted and carries another
        // recruiter's data; a dead refresh token must not leave it for the
        // next person on this machine. See `clearStoredSelectedImport`.
        clearStoredSelectedImport();
        window.location.assign("/login");
      },
    });
  const doFetch: FetchLike = (input, init) => authedFetch(input, init);

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    signal?: AbortSignal
  ): Promise<T> {
    const url = `${cfg.apiBase}${path}`;
    const res = await doFetch(url, {
      method,
      credentials: "include",
      headers: body !== undefined ? { "content-type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
    if (!res.ok) {
      // FastAPI puts the human-readable reason in `detail`. Reading it here
      // is what lets a 400 be shown verbatim ("People list title cannot be
      // empty.") instead of the generic status line. Best effort: an HTML
      // error page from the ALB has no JSON body, and losing the message is
      // strictly better than losing the error.
      let detail: string | undefined;
      let validationMessages: string[] | undefined;
      let detailData: Record<string, unknown> | undefined;
      try {
        const body = (await res.clone().json()) as { detail?: unknown; reason?: unknown };
        if (typeof body?.detail === "string") {
          detail = body.detail;
          // A string `detail` with a machine-readable sibling — the Thrive
          // preview's 503 `{detail, reason}` — keeps the sibling, so a caller
          // can tell "try again" from "this will time out again".
          if (typeof body.reason === "string") detailData = { reason: body.reason };
        } else if (body?.detail && typeof body.detail === "object" && !Array.isArray(body.detail)) {
          // A STRUCTURED detail (e.g. the Combine 413 carrying both numbers):
          // kept whole in `detailData`, and its `message` doubles as `detail`.
          detailData = body.detail as Record<string, unknown>;
          if (typeof detailData.message === "string") detail = detailData.message;
        } else if (Array.isArray(body?.detail)) {
          // FastAPI's VALIDATION errors (422) put `detail` as a LIST of
          // `{loc, msg, type}` objects, not a string — so the string-only
          // branch above dropped every one of them and the user got the bare
          // "Imports API request failed (422)" status line. That is not a
          // hypothetical: `CompareRequest.contact_ids` now carries a
          // `max_length`, and a selection over it is the first 422 a recruiter
          // can actually provoke. A cap whose refusal is unreadable is not
          // better than the edge's silent 403 it was meant to improve on.
          //
          // INTO `validationMessages`, NOT into `detail`. An earlier version of
          // this put them in `detail` and broke a different feature:
          // `useNewImport` reads `typeof detail === "string"` to separate "the
          // server declined your file" (terminal) from "client/server drift"
          // (retryable), so collapsing the two shapes turned every drift-422 on
          // `/complete` into a dead-end screen showing raw FastAPI text. The
          // field that carries the distinction has to keep carrying it; the new
          // information gets its own field. Found by /pr-review iter 2.
          //
          // `msg` alone — `loc` is wire-shape trivia ("body", "contact_ids")
          // that means nothing to a recruiter, and FastAPI's own message
          // ("List should have at most 200 items") is the useful half.
          // Defensive on the element shape: this is an error path, and throwing
          // inside the error handler would lose the error entirely.
          const msgs = body.detail
            .map((d) => (d as { msg?: unknown })?.msg)
            .filter((m): m is string => typeof m === "string");
          if (msgs.length > 0) validationMessages = msgs;
        }
      } catch {
        // not JSON — fall through to the generic message
      }
      throw new ImportsApiError(res.status, url, detail, validationMessages, detailData);
    }
    return (await res.json()) as T;
  }

  return {
    /** POST /imports — create import + presigned PUT (handshake step 1). */
    createImport(body: CreateImportPayload): Promise<ImportCreated> {
      return request<ImportCreated>("POST", PREFIX, body);
    },

    /** POST /imports/{id}/complete — finalize + enqueue parse (202).
     * Atomic create-on-complete: the row is inserted server-side here, so the
     * client echoes back the `s3_key` + metadata from the create step. */
    completeImport(importId: string, body: ImportCompleteRequest): Promise<AcceptedJob> {
      return request<AcceptedJob>("POST", `${PREFIX}/${importId}/complete`, body);
    },

    /** GET /imports/{id} — detail / parse poll target. */
    getImport(importId: string): Promise<ImportDetail> {
      return request<ImportDetail>("GET", `${PREFIX}/${importId}`);
    },

    /** GET /imports/{id}/lineage — the Full history trail.
     *
     * ONE request for the whole ancestor chain, root first. The alternative —
     * following `parent_import_id` with repeated `getImport` calls — is a
     * serial waterfall whose trail grows a segment at a time on screen.
     *
     * The chain is everything the server could resolve, not a completeness
     * guarantee: a dangling ancestor truncates it rather than failing. */
    getLineage(importId: string): Promise<ImportLineage> {
      return request<ImportLineage>("GET", `${PREFIX}/${importId}/lineage`);
    },

    /** PATCH /imports/{id} — rename the contact list.
     *
     * Send the STEM; the server re-applies the stored file's real extension
     * and echoes back what it actually stored, which is what callers must
     * render. A 400 carries the reason in `ImportsApiError.message`. */
    renameImport(importId: string, filename: string): Promise<ImportRenamed> {
      return request<ImportRenamed>("PATCH", `${PREFIX}/${importId}`, {
        filename,
      });
    },

    /** GET /imports — firm-wide list. */
    listImports(params: ListImportsParams = {}): Promise<ImportListResponse> {
      return request<ImportListResponse>("GET", `${PREFIX}${buildQuery(params)}`);
    },

    /** GET /imports/owners — the "Imported by" scope options.
     *  Takes no params: the option set is deliberately independent of the
     *  list's owner / tag / search / page state. */
    listImportOwners(): Promise<ImportOwnersResponse> {
      return request<ImportOwnersResponse>("GET", `${PREFIX}/owners`);
    },

    /** GET /searches — Thrive search options for the Link-to-Search picker.
     * `q` is omitted from the query string when blank (the initial view). */
    listSearches(params: ListSearchesParams = {}): Promise<SearchListResponseDTO> {
      return request<SearchListResponseDTO>(
        "GET",
        `/api/web/v2/searches${buildQuery({
          ...(params.q ? { q: params.q } : {}),
          ...(params.statuses ? { statuses: params.statuses } : {}),
        })}`
      );
    },

    /** GET /searches/{id}/thrive-preview — a search's candidacies per stage,
     *  read before pulling it in. A 503 carries `reason`
     *  (`rate_limited` | `deadline`) in `ImportsApiError.detailData`; a 502
     *  carries `retryable` there. */
    getThrivePreview(searchId: string): Promise<ThrivePreviewResponse> {
      return request<ThrivePreviewResponse>(
        "GET",
        `/api/web/v2/searches/${encodeURIComponent(searchId)}/thrive-preview`
      );
    },

    /** POST /imports/thrive-search — mint a raw People List from a Thrive
     *  search's candidacies (202). Poll `import_id` like an upload. */
    createThriveSearchImport(body: ThriveSearchImportRequest): Promise<ThriveSearchImportAccepted> {
      return request<ThriveSearchImportAccepted>("POST", `${PREFIX}/thrive-search`, body);
    },

    /** GET /imports/{id}/contacts — paginated, server-filtered contacts +
     *  dynamic columns + whole-import chip counts. `signal` lets TanStack
     *  cancel a superseded fetch (the multi-request whole-import loop would
     *  otherwise keep paging after its query key died). */
    listContacts(
      importId: string,
      params: ContactsQueryParams = {},
      opts: { signal?: AbortSignal } = {}
    ): Promise<ImportContactsResponse> {
      return request<ImportContactsResponse>(
        "GET",
        `${PREFIX}/${importId}/contacts${buildQuery(params)}`,
        undefined,
        opts.signal
      );
    },

    /** POST /imports/{id}/compare — enqueue dry-run Compare (202). */
    startCompare(importId: string, body?: CompareStartInput): Promise<AcceptedJob> {
      return request<AcceptedJob>("POST", `${PREFIX}/${importId}/compare`, body);
    },

    /** GET /imports/{id}/compare — Compare poll (dedup_status). */
    getCompare(importId: string): Promise<CompareResult> {
      return request<CompareResult>("GET", `${PREFIX}/${importId}/compare`);
    },

    /** GET /imports/compare-status — batched, lightweight Compare status for
     *  UP TO 10 imports at once (epic 2026-09-28-combine-perf-batch-1,
     *  sub-task 01, item 7). `useCompareSources`' poll loop (sub-task 02)
     *  calls this once per tick for every list it is tracking instead of one
     *  `getCompare` per list; a caller tracking more than 10 ids must chunk
     *  its own calls (`useCompareSources` does). An id with no Compare run,
     *  or that names no import, is simply ABSENT from the response array —
     *  no per-id 404 on a batched read, and NEVER treated as an error by the
     *  caller (see `useCompareSources`'s own handling of "absent"). Omitted
     *  or empty `ids` is a valid, empty-response call. */
    getCompareStatus(ids: readonly string[]): Promise<CompareStatusItem[]> {
      return request<CompareStatusItem[]>(
        "GET",
        `/api/web/v2/imports/compare-status${buildQuery({ ids })}`
      );
    },

    /** PUT /imports/{id}/contacts/{contactId}/leave-out — persist the
     *  'Leave out' dismissal. Every export path (incl. bulk "Add everyone")
     *  skips the contact SERVER-SIDE; this call is the source of that fact.
     *  Idempotent (original attribution kept). */
    leaveOut(importId: string, contactId: string): Promise<LeaveOutState> {
      return request<LeaveOutState>("PUT", `${PREFIX}/${importId}/contacts/${contactId}/leave-out`);
    },

    /** DELETE /imports/{id}/contacts/{contactId}/leave-out — undo, restoring
     *  the contact to the normal flow. */
    undoLeaveOut(importId: string, contactId: string): Promise<LeaveOutState> {
      return request<LeaveOutState>(
        "DELETE",
        `${PREFIX}/${importId}/contacts/${contactId}/leave-out`
      );
    },

    /** POST /imports/{id}/contacts/{contactId}/different-person — record the
     *  FIRM-WIDE registry pair (canonical linkedin_url <-> thrive_id). 409
     *  when the contact has no LinkedIn URL (no registry identity — the card
     *  disables the action, this is the server back-stop). */
    markDifferentPerson(
      importId: string,
      contactId: string,
      thriveId: string
    ): Promise<DifferentPersonExclusion> {
      return request<DifferentPersonExclusion>(
        "POST",
        `${PREFIX}/${importId}/contacts/${contactId}/different-person`,
        { thrive_id: thriveId }
      );
    },

    /** DELETE /imports/{id}/contacts/{contactId}/different-person — retire the
     *  FIRM-WIDE registry pair. Undoes the mark everywhere, not just for this
     *  list: the pair was never scoped to one import. Idempotent — `removed`
     *  is false when the pair was already gone (Compare retires pairs itself
     *  once the URL and the id stop colliding in Thrive). */
    undoDifferentPerson(
      importId: string,
      contactId: string,
      thriveId: string
    ): Promise<DifferentPersonUndone> {
      return request<DifferentPersonUndone>(
        "DELETE",
        `${PREFIX}/${importId}/contacts/${contactId}/different-person`,
        { thrive_id: thriveId }
      );
    },

    /** POST /imports/{id}/contacts/{contactId}/recheck — "did the cleanup
     *  work?" on a duplicate-LinkedIn card. ONE Thrive read, synchronous, no
     *  request body.
     *
     *  ONE READ PER CLICK. There is deliberately no retry and no poll here: the
     *  caller disables the option while this promise is in flight, and a failed
     *  read comes back as a 200 carrying `outcome: "check_failed"` rather than
     *  as something worth retrying automatically. A retry loop on a Thrive read
     *  fired from a radio row is how one recruiter becomes N reads.
     *
     *  409 when the contact has no LinkedIn URL to probe by; 404 when it is not
     *  in this import. Both surface as `ImportsApiError` — they are genuine
     *  failures, unlike `check_failed`. */
    /**
     * `POST .../contacts/{id}/add-to-search` — ONE contact, and it WAITS.
     *
     * A 200 with a real outcome, unlike its batch sibling's 202. The compare
     * modal celebrates and retires a row on the strength of this, and neither
     * is honest from an acknowledgement that a job was queued.
     *
     * READ `outcome`, and note that `pending` IS NOT A FAILURE: the job is
     * still running and the row must be left where it is.
     */
    addOneContactToSearch(
      importId: string,
      contactId: string,
      w2Decision?: string,
      /** CONFIRM an unverified name match before writing — see the route's own
       *  `confirm_thrive_id`. Only meaningful with `update_anyway`. */
      confirmThriveId?: string | null
    ): Promise<SingleContactExport> {
      const params = new URLSearchParams();
      if (w2Decision) params.set("w2_decision", w2Decision);
      if (confirmThriveId) params.set("confirm_thrive_id", confirmThriveId);
      const qs = params.toString() ? `?${params}` : "";
      return request<SingleContactExport>(
        "POST",
        `${PREFIX}/${importId}/contacts/${contactId}/add-to-search${qs}`
      );
    },

    recheckDuplicateLinkedIn(
      importId: string,
      contactId: string,
      /** WHICH question — see the server's `RecheckMode`. The two cards that
       *  fire this ask different ones and the threshold differs: a merge leaves
       *  one contact and that is success, while a pending create can tolerate
       *  none. Defaulted to the STRICTER rule so a forgotten argument
       *  under-reports success rather than green-lighting a duplicate. */
      mode: RecheckMode = "different_person"
    ): Promise<DuplicateLinkedInRecheck> {
      const qs = mode === "different_person" ? "" : `?mode=${mode}`;
      return request<DuplicateLinkedInRecheck>(
        "POST",
        `${PREFIX}/${importId}/contacts/${contactId}/recheck${qs}`
      );
    },

    /** POST /imports/{id}/add-to-search — write to Thrive AND the search (202).
     *
     *  Replaces `startExport`. The route now carries the intent that the worker
     *  used to infer from `imports.source`, and the server 409s a Company Map
     *  here. */
    addToSearch(importId: string, body: AddToSearchRequest): Promise<AddAccepted> {
      return request<AddAccepted>("POST", `${PREFIX}/${importId}/add-to-search`, body);
    },

    /** POST /imports/{id}/add-to-thrive — Company Map only; no candidacy (202). */
    addToThrive(importId: string, body: AddToThriveRequest): Promise<AddAccepted> {
      return request<AddAccepted>("POST", `${PREFIX}/${importId}/add-to-thrive`, body);
    },

    /** POST /imports/{id}/add-preview — what the Add would do (200, no write).
     *
     *  TAKES THE WRITE'S OWN BODY TYPE, not a preview-shaped one. The server
     *  model is `extra="ignore"` and carries no `search_id_thrive`, so the
     *  caller hands the SAME object to this and to `addToSearch`/`addToThrive`
     *  and the field is dropped. That is what makes it impossible for the
     *  preview to describe a selection the run will not make — pinned server
     *  side by a router test. Typing the parameter as the preview's own request
     *  would let the two drift apart again by construction. */
    previewAdd(importId: string, body: AddToSearchRequest): Promise<AddPreview> {
      return request<AddPreview>("POST", `${PREFIX}/${importId}/add-preview`, body);
    },

    /** GET /imports/{id}/export — the run poll, SHARED by both writes.
     *  Deliberately one endpoint: the run state is a property of the import,
     *  not of the route that started it, and on a cold page load the page polls
     *  a run it did not start. */
    getExport(importId: string): Promise<ExportStatus> {
      return request<ExportStatus>("GET", `${PREFIX}/${importId}/export`);
    },

    /** GET /imports/{id}/download — presigned GET URL for the original file. */
    getDownloadLink(importId: string): Promise<DownloadLink> {
      return request<DownloadLink>("GET", `${PREFIX}/${importId}/download`);
    },

    /**
     * POST /imports/combine/preview — synchronous funnel + a page of the
     * merged result (Pattern 2, no write). `signal` lets `useCombinePreview`
     * cancel a superseded request when the recipe changes mid-debounce.
     *
     * The server 413s when `source_contacts_total` (sum of the selected
     * sources' `total_contacts`) would exceed `max_source_contacts` — the
     * caller reads `ImportsApiError.status === 413` to show the over-limit
     * copy rather than a generic failure.
     */
    combinePreview(
      body: CombinePreviewInput,
      signal?: AbortSignal
    ): Promise<CombinePreviewResponse> {
      return request<CombinePreviewResponse>("POST", `${PREFIX}/combine/preview`, body, signal);
    },

    /**
     * POST /imports/combine — inserts the child import (`status='parsing'`)
     * and dispatches `combine_worker` (202, Pattern 3). Poll the returned
     * `import_id` via `getImport`, same as `completeImport`.
     *
     * The server 422s when `search_id_thrive` is missing or does not exist —
     * the caller reads `ImportsApiError.status === 422` to show "Pick a
     * search" rather than a generic failure. It 409s with a structured
     * `detail` (`CombineConflictDetail`, kept in `ImportsApiError.detailData`)
     * whose `code` says why: `source_not_ready`, `compare_in_flight` (with
     * `remove_in_thrive` on, a source's Compare is still queued/running),
     * `target_check_pending` (a source is still being checked against the
     * target search in the background) or `stale_exclusions` (an untick no
     * longer names anyone). It 503s with a string `detail` when no merge
     * slot came in time. It never calls Thrive.
     */
    combineGenerate(body: CombineRequest): Promise<CombineAccepted> {
      return request<CombineAccepted>("POST", `${PREFIX}/combine`, body);
    },

    /**
     * POST /imports/{id}/contacts/leave-out/bulk — the "N already in search —
     * Remove them" banner action (sub-task 05). One UPDATE, sync (Pattern 2).
     */
    bulkLeaveOut(importId: string, body: BulkLeaveOutRequest): Promise<BulkLeaveOutResult> {
      return request<BulkLeaveOutResult>(
        "POST",
        `${PREFIX}/${importId}/contacts/leave-out/bulk`,
        body
      );
    },

    /** POST /avatars/presign — batch presign GET URLs for the durable stored
     *  avatar keys (`import_contacts.profile_image_s3_key`). Lets the UI render
     *  the durable S3 copy instead of the contact's expiring source photo URL —
     *  works past the 15-day avatar-cache TTL.
     *
     *  Chunks into `PRESIGN_MAX_KEYS_PER_REQUEST`-key requests so a full page can
     *  never exceed the ALB WAF request-body cap (see the constant), then merges
     *  the per-key URL maps back into one response. */
    async presignAvatars(s3Keys: string[]): Promise<AvatarPresignResponse> {
      const batches: string[][] = [];
      for (let i = 0; i < s3Keys.length; i += PRESIGN_MAX_KEYS_PER_REQUEST) {
        batches.push(s3Keys.slice(i, i + PRESIGN_MAX_KEYS_PER_REQUEST));
      }
      const results = await Promise.allSettled(
        batches.map((batch) =>
          request<AvatarPresignResponse>("POST", "/api/web/v2/avatars/presign", {
            s3_keys: batch,
          })
        )
      );
      const urls: Record<string, string> = {};
      let anyFulfilled = false;
      for (const r of results) {
        if (r.status === "fulfilled") {
          anyFulfilled = true;
          Object.assign(urls, r.value.urls);
        }
      }
      // If SOME chunks failed but others succeeded, degrade to a partial map —
      // the resolved contacts show their S3 avatar, the rest fall to initials —
      // rather than discarding a whole page over one chunk's transient 403. Only
      // when EVERY chunk failed do we surface the error, so `useResolveAvatars`
      // logs it (a wholesale outage still gets a signal, not silent initials).
      if (!anyFulfilled && batches.length > 0) {
        const firstRejected = results.find(
          (r): r is PromiseRejectedResult => r.status === "rejected"
        );
        throw firstRejected?.reason ?? new Error("presignAvatars: all batches failed");
      }
      return { urls };
    },
  };
}

export type ImportsClient = ReturnType<typeof createImportsClient>;
